import yup from "../../../../../utils/yup-extended";
import {Alert, Button, Space, Spin} from "antd";
import {StyledPublicProfileOfferingDrawer} from "../../../../public-profile/offering/offering.style";
import {CloseOutlined} from "@ant-design/icons";
import {LiveClassTimeReschedule} from "../time-reschedule";
import {CustomInputComponent} from "../../../../input/input-component";
import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import {ErrorBoundary} from "react-error-boundary";
import {useLiveEvent} from "../../../../../features/live-events";
import {CallDurationOptions} from "../../../../../container/onboarding/step-1/step-1-utils";

const messageSchema = yup.object().shape({
  message: yup.string().trim(),
});

type SessionInfo = {
  name: string;
  startDate: string;
  startTime: string;
  duration: string | number;
  eventId: string | number;
  customDuration: string | number;
};

type LiveClassRescheduleModalProps = {
  visible: boolean;
  onClose: () => void;
  eventId?: number;
};

export function LiveClassRescheduleModal({
  eventId,
  onClose,
  visible,
}: LiveClassRescheduleModalProps) {
  const {
    formState: {errors, isValid},
    control,
    reset,
    getValues,
    watch,
  } = useForm<{
    message: string;
  }>({
    resolver: yupResolver(messageSchema),
    mode: "onChange",
  });

  const eventQuery = useLiveEvent(eventId ?? 0);

  const buildSessionInfo = (): SessionInfo[] => {
    if (eventQuery.data?.sessionInfo) {
      return eventQuery.data.sessionInfo.map((x) => ({
        eventId: eventQuery.data!.id,
        customDuration: CallDurationOptions.find((ele) => ele.value === x.duration)
          ? 15
          : x.duration,
        duration:
          CallDurationOptions.find((ele) => Number(ele.value) === Number(x.duration)) !==
          undefined
            ? x.duration
            : "custom",
        name: x.name,
        startDate: x.startDate,
        startTime: x.startTime,
      }));
    }
    return [];
  };

  const sessionInfo = buildSessionInfo();
  const isLoading = !!eventId && eventQuery.isLoading;
  const isError = !eventId || eventQuery.isError;

  return (
    <StyledPublicProfileOfferingDrawer
      title={"Reschedule live event"}
      footer={null}
      visible={visible}
      onClose={() => {
        reset();
        onClose();
      }}
      maskClosable={true}
      closable={false}
      extra={
        <Button
          type="ghost"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
    >
      <ErrorBoundary
        FallbackComponent={({error, resetErrorBoundary}) => (
          <div style={{marginTop: "1.8rem"}}>
            <Alert
              message="Sorry, something went wrong."
              showIcon
              description={error.message}
              type="error"
              action={
                <Button
                  onClick={() => {
                    resetErrorBoundary();
                  }}
                  size="small"
                  danger
                >
                  Reset
                </Button>
              }
            />
          </div>
        )}
      >
        {isLoading && (
          <div style={{display: "flex", justifyContent: "center", padding: "3rem 0"}}>
            <Spin size="large" />
          </div>
        )}
        {isError && (
          <div style={{marginTop: "1.8rem"}}>
            <Alert
              message="Failed to load event details"
              showIcon
              description={
                !eventId
                  ? "Event ID is required to reschedule."
                  : eventQuery.error?.message ?? "Please try again later."
              }
              type="error"
              action={
                <Button onClick={onClose} size="small">
                  Close
                </Button>
              }
            />
          </div>
        )}
        {!isLoading && !isError && (
          <div>
            <Space direction="vertical" size={24} style={{display: "flex"}}>
              <div>
                <p>
                  We will notify your registered customers after you have set your new
                  date and time.
                </p>
                <CustomInputComponent
                  id="personalMessage"
                  name="message"
                  type="textarea"
                  label={`Include a personal message to your customers`}
                  placeholder="Type a message"
                  error={errors.message?.message}
                  control={control}
                />
              </div>
              <div>
                <Space direction="vertical" size={24} style={{display: "flex"}}>
                  {sessionInfo.map((sesh, index) => (
                    <LiveClassTimeReschedule
                      customDuration={sesh.customDuration}
                      duration={sesh.duration}
                      name={sesh.name}
                      startDate={sesh.startDate}
                      startTime={sesh.startTime}
                      eventId={sesh.eventId}
                      scheduleId={index}
                      allSessions={sessionInfo}
                      msgValid={isValid}
                      msgGetValues={getValues}
                      msgReset={reset}
                      msgWatch={watch}
                      closeModal={onClose}
                      key={index}
                    />
                  ))}
                </Space>
              </div>
            </Space>
          </div>
        )}
      </ErrorBoundary>
    </StyledPublicProfileOfferingDrawer>
  );
}
